/**
 * Inline-SVG genre fallback tiles for Foundry's dashboard.
 *
 * Issue #603 — see `src/shared/genre-fallback.ts` for the design.
 *
 * Each genre key gets a stylised gradient + decorative shapes that
 * communicate the mood (trivia = stage lights + question marks, word
 * = drifting letter tiles, sport = arc of motion, etc.). The SVGs
 * are inlined (no external file fetch) so they paint instantly even
 * for community games where the user has never had the corresponding
 * hero image locally. The gradient colours match the per-genre
 * `mood` strings in `scripts/generate-genre-fallbacks.mjs` so a
 * later Fal.ai-generated WebP can sit alongside the SVG without
 * jarring colour shifts.
 *
 * This is the BASE-LAYER fallback. The full priority chain (from
 * highest fidelity to lowest):
 *
 *   1. `game.heroImage` URL resolved by `main/index.ts`'s
 *      `resolveHeroImage` (game-specific Fal.ai art committed to
 *      the repo, or a bundled tile copied from ProtoHub) — renders
 *      via an `<img>` tag inside the card.
 *   2. ON ERROR: `<GenreFallback genreKey={…} />` renders the
 *      inline SVG for the inferred genre.
 *
 * The renderer's two card surfaces (`GameCard` and
 * `CommunityGameCard`) both consume this component. They infer the
 * genre key with `inferGenreKey(displayName, description)` from
 * `src/shared/genre-fallback.ts`.
 */
import { useId } from 'react';
import type { GenreKey } from '@volley/crucible-ipc-types/genre-fallback';

export interface GenreFallbackProps {
    /** Inferred via `inferGenreKey()`. Defaults to `'default'` so the
     * caller doesn't have to handle the "no signal yet" path explicitly. */
    genreKey: GenreKey;
    /** Optional alt text — usually the game's displayName. */
    alt?: string;
    /** Extra classes for the wrapping element. */
    className?: string;
}

interface GradientStops {
    readonly from: string;
    readonly via?: string;
    readonly to: string;
}

/** Per-genre colour palette. Hex codes chosen to feel cohesive with
 * Foundry's dark surface (`bg-crucible-surface` ≈ `#13141a`) and to
 * read clearly at the dashboard card's typical 320×180 footprint. */
const GRADIENTS: Record<GenreKey, GradientStops> = {
    trivia: { from: '#3b1d6b', via: '#7a3eaf', to: '#f3c45a' },
    word: { from: '#2a4d4a', via: '#3f8783', to: '#f0c47b' },
    puzzle: { from: '#1a2b4a', via: '#4a6fb3', to: '#9fc9ff' },
    twitch: { from: '#0e2330', via: '#1f9bd0', to: '#f15bb5' },
    party: { from: '#5a1f4d', via: '#d63384', to: '#ffba49' },
    narrative: { from: '#1c2733', via: '#56708a', to: '#d9b48b' },
    arcade: { from: '#2c0d36', via: '#ff5a5f', to: '#ffd166' },
    music: { from: '#1a1a2e', via: '#7c3aed', to: '#ff4d9d' },
    sport: { from: '#0d3b1f', via: '#2f8f4d', to: '#f3d35e' },
    default: { from: '#1d2a3b', via: '#3e5c7a', to: '#a8c4d9' },
};

/** Per-genre decorative shape painted on top of the gradient. Kept
 * abstract — no faces, no logos, no text — so the same component
 * works as a placeholder across very different game subgenres
 * within a single key (e.g. "Pop Trivia" vs "Sports Trivia"). */
function GenreOrnament({ genreKey }: { genreKey: GenreKey }): React.JSX.Element {
    switch (genreKey) {
        case 'trivia':
            return (
                <g opacity="0.85">
                    {/* Question mark cluster */}
                    <text x="180" y="170" fontSize="220" fontFamily="serif" fontWeight="bold" fill="#ffffff" opacity="0.18">?</text>
                    <text x="540" y="240" fontSize="160" fontFamily="serif" fontWeight="bold" fill="#ffffff" opacity="0.22">?</text>
                    <text x="800" y="160" fontSize="120" fontFamily="serif" fontWeight="bold" fill="#ffffff" opacity="0.16">?</text>
                    {/* Stage spotlight beam */}
                    <polygon points="500,0 350,576 650,576" fill="#ffffff" opacity="0.08" />
                </g>
            );
        case 'word':
            return (
                <g opacity="0.85">
                    {/* Drifting letter tiles */}
                    {[
                        { x: 120, y: 120, l: 'A', r: -8 },
                        { x: 280, y: 220, l: 'W', r: 6 },
                        { x: 460, y: 140, l: 'O', r: -3 },
                        { x: 640, y: 260, l: 'R', r: 10 },
                        { x: 820, y: 180, l: 'D', r: -6 },
                    ].map((t) => (
                        <g key={t.l} transform={`translate(${t.x},${t.y}) rotate(${t.r})`} opacity="0.55">
                            <rect width="100" height="100" rx="14" fill="#ffffff" opacity="0.10" />
                            <text x="50" y="72" fontSize="64" fontFamily="sans-serif" fontWeight="700" textAnchor="middle" fill="#ffffff" opacity="0.85">{t.l}</text>
                        </g>
                    ))}
                </g>
            );
        case 'puzzle':
            return (
                <g opacity="0.85" stroke="#ffffff" strokeOpacity="0.25" strokeWidth="2" fill="#ffffff" fillOpacity="0.10">
                    {/* Interlocking geometric shapes */}
                    <rect x="120" y="120" width="180" height="180" rx="20" />
                    <rect x="260" y="260" width="180" height="180" rx="20" />
                    <rect x="500" y="160" width="180" height="180" rx="20" />
                    <rect x="640" y="320" width="180" height="180" rx="20" />
                </g>
            );
        case 'twitch':
            return (
                <g opacity="0.85">
                    {/* Motion-blur streaks */}
                    {[80, 200, 320, 440].map((y) => (
                        <rect key={y} x="0" y={y} width="1024" height="4" fill="#ffffff" opacity={0.10 + (y / 4000)} />
                    ))}
                    {/* Lightning bolt accent */}
                    <polygon points="540,80 460,300 540,300 460,500 700,250 580,250 660,80" fill="#ffffff" opacity="0.22" />
                </g>
            );
        case 'party':
            return (
                <g opacity="0.85">
                    {/* Confetti pieces */}
                    {[
                        { x: 120, y: 80, w: 24, h: 12, c: '#ffffff', r: 20 },
                        { x: 220, y: 220, w: 18, h: 18, c: '#ffd166', r: -15 },
                        { x: 360, y: 140, w: 22, h: 10, c: '#ffffff', r: 35 },
                        { x: 500, y: 320, w: 16, h: 16, c: '#ff4d9d', r: 0 },
                        { x: 640, y: 100, w: 28, h: 12, c: '#ffffff', r: -25 },
                        { x: 780, y: 260, w: 20, h: 20, c: '#06d6a0', r: 12 },
                        { x: 880, y: 160, w: 14, h: 14, c: '#ffffff', r: 45 },
                        { x: 180, y: 420, w: 26, h: 10, c: '#ffba49', r: -8 },
                        { x: 420, y: 480, w: 16, h: 16, c: '#ffffff', r: 22 },
                        { x: 720, y: 460, w: 20, h: 10, c: '#ff4d9d', r: -40 },
                    ].map((c, i) => (
                        <rect key={i} x={c.x} y={c.y} width={c.w} height={c.h} fill={c.c} opacity="0.7" transform={`rotate(${c.r} ${c.x + c.w / 2} ${c.y + c.h / 2})`} />
                    ))}
                </g>
            );
        case 'narrative':
            return (
                <g opacity="0.85">
                    {/* A winding path stretching into depth */}
                    <path
                        d="M 0 500 C 200 480, 360 380, 512 360 S 800 280, 1024 200"
                        stroke="#ffffff"
                        strokeOpacity="0.30"
                        strokeWidth="4"
                        fill="none"
                    />
                    <path
                        d="M 0 540 C 220 520, 400 420, 540 400 S 820 320, 1024 240"
                        stroke="#ffffff"
                        strokeOpacity="0.15"
                        strokeWidth="2"
                        fill="none"
                    />
                </g>
            );
        case 'arcade':
            return (
                <g opacity="0.85">
                    {/* Retro pixel ship + scanlines */}
                    {Array.from({ length: 18 }).map((_, i) => (
                        <rect key={i} x="0" y={i * 36} width="1024" height="2" fill="#ffffff" opacity="0.08" />
                    ))}
                    {/* Pixel ship in middle */}
                    <g transform="translate(456,240) scale(2)" fill="#ffffff" opacity="0.6">
                        <rect x="20" y="0" width="20" height="20" />
                        <rect x="0" y="20" width="60" height="20" />
                        <rect x="-20" y="40" width="100" height="20" />
                        <rect x="-20" y="60" width="20" height="20" />
                        <rect x="60" y="60" width="20" height="20" />
                    </g>
                </g>
            );
        case 'music':
            return (
                <g opacity="0.85">
                    {/* Sound wave bars */}
                    {Array.from({ length: 24 }).map((_, i) => {
                        const h = 40 + Math.abs(Math.sin(i * 0.6)) * 220;
                        return (
                            <rect
                                key={i}
                                x={60 + i * 38}
                                y={288 - h / 2}
                                width="22"
                                height={h}
                                rx="11"
                                fill="#ffffff"
                                opacity="0.30"
                            />
                        );
                    })}
                </g>
            );
        case 'sport':
            return (
                <g opacity="0.85">
                    {/* Arc of motion */}
                    <path
                        d="M 60 480 Q 512 80, 960 480"
                        stroke="#ffffff"
                        strokeOpacity="0.45"
                        strokeWidth="6"
                        strokeDasharray="2 14"
                        fill="none"
                    />
                    {/* Ball at end of arc */}
                    <circle cx="960" cy="480" r="32" fill="#ffffff" opacity="0.85" />
                    <circle cx="960" cy="480" r="32" fill="none" stroke="#0d3b1f" strokeWidth="2" opacity="0.6" />
                </g>
            );
        case 'default':
        default:
            return (
                <g opacity="0.85">
                    {/* Abstract drifting shapes */}
                    <circle cx="220" cy="200" r="120" fill="#ffffff" opacity="0.10" />
                    <circle cx="700" cy="320" r="160" fill="#ffffff" opacity="0.08" />
                    <circle cx="900" cy="140" r="60" fill="#ffffff" opacity="0.12" />
                </g>
            );
    }
}

/**
 * Render a stylised genre placeholder. Used by `GameCard` and
 * `CommunityGameCard` when the game's own hero image is missing or
 * fails to load.
 */
export function GenreFallback({ genreKey, alt, className }: GenreFallbackProps): React.JSX.Element {
    const gradient = GRADIENTS[genreKey];
    // Codex round-1 Bugbot + Macroscope finding: the dashboard
    // renders many `<GenreFallback>` instances at once, all sharing
    // the document's SVG-ID namespace. The earlier draft used
    // `id="genre-grad-${genreKey}"` (collides between two trivia
    // cards) and a hardcoded `id="vignette"` (collides between
    // every card). `url(#id)` resolves to whichever `id` was parsed
    // first; if that first card unmounts the rest lose their
    // gradient reference until React re-renders. `useId()` returns
    // a stable per-instance prefix that survives StrictMode's
    // double-render and SSR, so the IDs we mint here can never
    // collide across the page.
    const uid = useId();
    const gradId = `genre-grad-${genreKey}-${uid}`;
    const vignetteId = `genre-vignette-${uid}`;

    return (
        <svg
            viewBox="0 0 1024 576"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
            className={className}
            role="img"
            aria-label={alt ?? `${genreKey} game placeholder`}
            data-testid={`genre-fallback-${genreKey}`}
        >
            <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={gradient.from} />
                    {gradient.via != null && <stop offset="50%" stopColor={gradient.via} />}
                    <stop offset="100%" stopColor={gradient.to} />
                </linearGradient>
                <radialGradient id={vignetteId} cx="50%" cy="50%" r="75%">
                    <stop offset="60%" stopColor="black" stopOpacity="0" />
                    <stop offset="100%" stopColor="black" stopOpacity="0.5" />
                </radialGradient>
            </defs>
            <rect width="1024" height="576" fill={`url(#${gradId})`} />
            <GenreOrnament genreKey={genreKey} />
            {/* Subtle vignette to soften the edges against the card border */}
            <rect width="1024" height="576" fill={`url(#${vignetteId})`} opacity="0.4" />
        </svg>
    );
}
